import React, { useState } from 'react';

const CEAssignmentTable = ({ assignments, onSingleUpload }) => {
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [uploadResults, setUploadResults] = useState({});
    const [wordCounts, setWordCounts] = useState({});

    const handleWordCountChange = (articleId, value) => {
        setWordCounts(prev => ({
            ...prev,
            [articleId]: value
        }));
    };

    const handleFileUpload = async (articleId, event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.toLowerCase().endsWith('.docx')) {
            setUploadResults({
                ...uploadResults,
                [articleId]: { success: false, errors: ['Only DOCX files are allowed'] }
            });
            return;
        }

        setUploadingFiles({ ...uploadingFiles, [articleId]: { uploading: true, progress: 0 } });
        setUploadResults({ ...uploadResults, [articleId]: null });

        try {
            const result = await onSingleUpload(articleId, file, wordCounts[articleId], (progress) => {
                setUploadingFiles(prev => ({
                    ...prev,
                    [articleId]: { uploading: true, progress }
                }));
            });
            setUploadResults({ ...uploadResults, [articleId]: result });
            if (result.success) {
                setWordCounts(prev => {
                    const newCounts = { ...prev };
                    delete newCounts[articleId];
                    return newCounts;
                });
            }
        } catch (error) {
            setUploadResults({
                ...uploadResults,
                [articleId]: { success: false, errors: ['Upload failed'] }
            });
        } finally {
            setUploadingFiles(prev => ({ ...prev, [articleId]: false }));
            // Clear file input
            event.target.value = '';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'N/A' || dateString === 'No deadline') return dateString || 'N/A';

        // If it's already in "DD MMM YYYY" format (contains letters), return as is
        if (/[a-zA-Z]/.test(dateString)) return dateString;

        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    const getPriorityBadge = (priority) => {
        const colors = {
            HIGH: 'bg-red-100 text-red-800',
            MEDIUM: 'bg-yellow-100 text-yellow-800',
            LOW: 'bg-green-100 text-green-800'
        };
        return colors[priority] || colors.MEDIUM;
    };

    const getStatusBadge = (status) => {
        const s = (status || '').toLowerCase();
        const colors = {
            'assigned': 'bg-blue-100 text-blue-800',
            'ce submitted': 'bg-green-100 text-green-800',
            'completed': 'bg-purple-100 text-purple-800'
        };
        return colors[s] || 'bg-gray-100 text-gray-800';
    };

    if (assignments.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-500">
                    <p className="text-lg">No assignments found</p>
                    <p className="text-sm mt-2">You don't have any articles assigned at the moment.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Article ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Journal
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Assigned Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Deadline
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Upload
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {assignments.map((assignment) => (
                        <tr key={assignment.articleId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                {assignment.articleId}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {assignment.journal}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex flex-col">
                                    <span>{formatDate(assignment.assignedDate)}</span>
                                    {assignment.downloadLink && (
                                        <a 
                                            href={assignment.downloadLink} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-xs text-purple-600 hover:text-purple-800 font-bold mt-1 flex items-center"
                                        >
                                            <span className="mr-1"> {assignment.isS3 ? '📦' : '🔗'}</span>
                                            Download Files
                                        </a>
                                    )}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${getStatusBadge(assignment.status)}`}>
                                    {assignment.status}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(assignment.deadline)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${getPriorityBadge(assignment.priority)}`}>
                                    {assignment.priority}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex flex-col space-y-2">
                                    {assignment.status && assignment.status.toLowerCase() === 'assigned' ? (
                                        <div className="flex flex-col space-y-2">
                                            <input
                                                type="number"
                                                min="1"
                                                required
                                                placeholder="Word count (required)"
                                                className={`text-xs rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 ${!wordCounts[assignment.articleId] ? 'border-red-300' : 'border-gray-300'}`}
                                                value={wordCounts[assignment.articleId] || ''}
                                                onChange={(e) => handleWordCountChange(assignment.articleId, e.target.value)}
                                                disabled={uploadingFiles[assignment.articleId]?.uploading}
                                            />
                                            <div>
                                                <input
                                                    type="file"
                                                    accept=".docx"
                                                    onChange={(e) => handleFileUpload(assignment.articleId, e)}
                                                    disabled={uploadingFiles[assignment.articleId]?.uploading || !wordCounts[assignment.articleId] || wordCounts[assignment.articleId] <= 0}
                                                    className="hidden"
                                                    id={`upload-${assignment.articleId}`}
                                                />
                                                <label
                                                    htmlFor={`upload-${assignment.articleId}`}
                                                    className={`cursor-pointer inline-flex items-center px-4 py-1.5 border border-transparent text-xs font-bold rounded-lg text-white shadow-sm transition-all w-full justify-center active:scale-95 ${uploadingFiles[assignment.articleId]?.uploading || !wordCounts[assignment.articleId] || wordCounts[assignment.articleId] <= 0
                                                            ? 'bg-gray-400 cursor-not-allowed'
                                                            : 'bg-purple-600 hover:bg-purple-700 shadow-purple-100 hover:shadow-purple-200'
                                                        }`}
                                                    title={(!wordCounts[assignment.articleId] || wordCounts[assignment.articleId] <= 0) ? "Please enter a valid word count first" : ""}
                                                >
                                                    {uploadingFiles[assignment.articleId]?.uploading ? `⏳ Uploading... ${uploadingFiles[assignment.articleId]?.progress || 0}%` : '📤 Upload'}
                                                </label>
                                                {uploadingFiles[assignment.articleId]?.uploading && (
                                                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                                        <div className="bg-purple-600 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadingFiles[assignment.articleId]?.progress || 0}%` }}></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-green-600 font-bold flex items-center">
                                            <span className="mr-1">✅</span>
                                            Submitted
                                        </span>
                                    )}

                                    {/* Upload result */}
                                    {uploadResults[assignment.articleId] && (
                                        <div className={`text-xs mt-1 max-w-[200px] ${uploadResults[assignment.articleId].success ? 'text-green-600' : 'text-red-600'}`}>
                                            {uploadResults[assignment.articleId].success ? (
                                                '✅ Uploaded successfully!'
                                            ) : (
                                                <div className="font-medium">
                                                    ❌ Failed: {uploadResults[assignment.articleId].errors?.[0] || 'Unknown error'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default CEAssignmentTable;
